/**
 * Phase 5-A enhancement — Recent Strategies tray on the /strategy wizard
 * landing. Reads `GET /api/strategy/generations`, surfaces up to 5 most
 * recent generations with a one-click Replay that fetches
 * `GET /api/strategy/generations/{id}` and hands the resulting roadmap back
 * to the parent (StrategyPage) which swaps into the Roadmap view.
 *
 * No LLM cost — pure Mongo replay. Hidden when the user has no generations
 * yet so it doesn't add visual noise on first use.
 */

import { formatDistanceToNow } from "date-fns";
import { History, Loader2, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";

export default function RecentStrategiesTray({ onReplay }) {
    const [items, setItems] = useState(null); // null = loading, [] = empty
    const [replayingId, setReplayingId] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/strategy/generations");
            setItems(data?.items || []);
        } catch (err) {
            // Non-fatal — just hide the tray.
            // eslint-disable-next-line no-console
            console.warn("[recent-strategies] fetch failed", err);
            setItems([]);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleReplay = async (gen) => {
        if (replayingId) return;
        setReplayingId(gen.generation_id);
        try {
            const { data } = await api.get(
                `/strategy/generations/${gen.generation_id}`
            );
            onReplay?.(data);
        } catch (err) {
            toast.error("Couldn't open that strategy", {
                description: err?.response?.data?.detail || err?.message,
            });
        } finally {
            setReplayingId(null);
        }
    };

    // Hide the entire tray while loading or if empty — zero visual noise.
    if (!items || items.length === 0) return null;

    return (
        <section
            className="flex flex-col gap-3 rounded-2xl border border-[#1F2A40] bg-[#111726]/70 p-4"
            data-testid="strategy-recent-tray"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <History className="h-3.5 w-3.5 text-[#00F5D4]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8A93A6]">
                        recent strategies
                    </span>
                </div>
                <span
                    className="font-mono text-[10px] tabular-nums text-[#8A93A6]"
                    data-testid="strategy-recent-count"
                >
                    {items.length}
                </span>
            </div>

            <ul className="flex flex-col divide-y divide-[#1F2A40]/60">
                {items.slice(0, 5).map((g) => {
                    const rel = (() => {
                        try {
                            return formatDistanceToNow(new Date(g.created_at), {
                                addSuffix: true,
                            });
                        } catch {
                            return "recently";
                        }
                    })();
                    const isReplaying = replayingId === g.generation_id;
                    return (
                        <li
                            key={g.generation_id}
                            className="flex items-center justify-between gap-3 py-2.5"
                            data-testid={`strategy-recent-item-${g.generation_id}`}
                        >
                            <div className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-sm text-[#E8ECF5]">
                                    {g.niche}
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A93A6]">
                                    {g.duration_days} days · {rel}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleReplay(g)}
                                disabled={!!replayingId}
                                data-testid={`strategy-recent-replay-${g.generation_id}`}
                                className={[
                                    "inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-all",
                                    isReplaying
                                        ? "cursor-wait border-[#00F5D4]/40 bg-[#00F5D4]/10 text-[#00F5D4]"
                                        : "border-[#1F2A40] bg-[#0B0F19] text-[#E8ECF5] hover:border-[#00F5D4]/50 hover:text-[#00F5D4]",
                                ].join(" ")}
                            >
                                {isReplaying ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <RotateCcw className="h-3 w-3" />
                                )}
                                {isReplaying ? "Opening…" : "Replay"}
                            </button>
                        </li>
                    );
                })}
            </ul>
            <p className="font-mono text-[10px] italic text-[#8A93A6]">
                Replays are free — pure data, no new generation.
            </p>
        </section>
    );
}
